import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Eye, ThumbsUp, MessageCircle, Clock, Flame, Bookmark, Loader2, Youtube } from "lucide-react";
import { API, trackedVideoUrl, timeAgo } from "@/lib/config";
import { useCountry } from "@/components/contexts/CountryContext";
import { CompactGrid } from "@/components/VideoCardCompact";
import { ShareMenu } from "@/components/ShareMenu";
import { FollowButton } from "@/components/FollowButton";
import { recordWatch, isSaved as isVideoSaved, toggleSaved as toggleVideoSaved } from "@/lib/storage";
import { PlayerSlot } from "@/components/player/PlayerSlot";

/**
 * A page of its own for one video.
 *
 * Until now a video only ever existed inside a modal, which meant no URL to share,
 * nothing for a crawler to index, and no way back to a video someone remembered.
 * The document title and canonical link are set here for people and for in-app
 * navigation; crawlers are served real meta tags by the backend (see
 * /video-meta/{id}), because a single-page bundle cannot set them before the
 * request is answered.
 */
export const VideoPage = () => {
  const { videoId } = useParams();
  const { country } = useCountry();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/video/${videoId}`, {
          params: { country: country.code, related: 12 },
        });
        if (cancelled) return;
        setData(res.data);
        setSaved(isVideoSaved(res.data.video.id));
        recordWatch(res.data.video);
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.status === 404 ? "That video is not available." : "Could not load this video.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [videoId, country.code]);

  // Title and canonical follow the video so shared and bookmarked links read
  // correctly, and so the tab is not just "TrendingZ" on every page.
  useEffect(() => {
    const previousTitle = document.title;
    if (data?.video) {
      document.title = `${data.video.title} — TrendingZ`;
    }

    const link = document.querySelector('link[rel="canonical"]');
    const previousHref = link?.getAttribute("href");
    if (link && data?.video) {
      link.setAttribute("href", `${window.location.origin}/video/${data.video.id}`);
    }

    return () => {
      document.title = previousTitle;
      if (link && previousHref) link.setAttribute("href", previousHref);
    };
  }, [data]);

  // PiP eligibility and the feed's silence are the global player's business now:
  // both have to outlive this page, because the mini player does.

  const handleSave = useCallback(() => {
    if (data?.video) setSaved(toggleVideoSaved(data.video));
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex justify-center" data-testid="video-page-loading">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen pt-24 px-4 text-center" data-testid="video-page-error">
        <p className="text-white/60 mb-4">{error}</p>
        <Link to="/" className="text-orange-400 hover:text-orange-300">
          Back to trending
        </Link>
      </div>
    );
  }

  const { video, related, chart_rank: chartRank } = data;
  const stats = [
    { icon: Eye, label: "views", value: video.view_count },
    { icon: ThumbsUp, label: "likes", value: video.like_count },
    {
      icon: MessageCircle,
      label: "comments",
      value: video.comment_count_raw ? video.comment_count_raw.toLocaleString() : null,
    },
    { icon: Clock, label: "published", value: timeAgo(video.published_at) },
  ].filter((s) => s.value);

  return (
    <div className="min-h-screen pt-20 pb-16" data-testid="video-page">
      <div className="max-w-5xl mx-auto px-4">
        {/* No embed of its own: the app has exactly one player and this reserves
            the space for it. Scrolling down past this box hands playback to the
            corner window, and scrolling back up returns it here - which only works
            because the iframe never moves in the DOM. */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
          <PlayerSlot
            video={video}
            surface="page"
            radius={16}
            className="rounded-2xl aspect-video"
          />
        </motion.div>

        <div className="flex flex-wrap items-start gap-3 mb-3">
          <h1 className="font-heading text-xl sm:text-2xl font-black flex-1 min-w-[240px]">
            {video.title}
          </h1>
          {chartRank > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-orange-500/15 border border-orange-500/30 text-orange-400 text-sm font-bold">
              <Flame className="w-4 h-4" />#{chartRank} in {country.name}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/50 mb-5">
          {stats.map(({ icon: Icon, label, value }) => (
            <span key={label} className="inline-flex items-center gap-1.5">
              <Icon className="w-4 h-4" />
              {value}
            </span>
          ))}
          {video.views_per_hour > 0 && (
            <span className="text-orange-400">{video.views_per_hour.toLocaleString()} views/hr</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 mb-6">
          <Link
            to={`/channel/${video.channel_id}`}
            className="flex items-center gap-3 min-w-0 flex-1 group"
          >
            {video.channel_avatar ? (
              <img src={video.channel_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center text-black font-bold">
                {video.channel_title?.charAt(0)?.toUpperCase() || "C"}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-medium truncate group-hover:text-orange-400 transition-colors">
                {video.channel_title}
              </p>
              {video.subscriber_count && (
                <p className="text-xs text-white/40">{video.subscriber_count} subscribers</p>
              )}
            </div>
          </Link>

          <FollowButton
            channel={{
              channel_id: video.channel_id,
              channel_title: video.channel_title,
              channel_avatar: video.channel_avatar,
            }}
            size="lg"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-10">
          <a
            href={trackedVideoUrl(video, country.code)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-yellow-500 text-black font-heading font-bold text-sm hover:opacity-90 transition-opacity"
          >
            <Youtube className="w-4 h-4" />
            Watch on YouTube
          </a>
          <button
            onClick={handleSave}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm hover:bg-white/10 transition-colors"
            aria-pressed={saved}
          >
            <Bookmark className={`w-4 h-4 ${saved ? "fill-orange-400 text-orange-400" : ""}`} />
            {saved ? "Saved" : "Save"}
          </button>
          <ShareMenu video={video} />
        </div>

        <h2 className="font-heading text-lg font-bold mb-4">Watch next</h2>
        <CompactGrid
          videos={related}
          linkTo={(v) => `/video/${v.id}`}
          emptyMessage="Nothing related to show."
        />
      </div>
    </div>
  );
};
